import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Play, Pause, Volume2, VolumeX, Maximize, X, Star,
  Mountain, Waves, TreePine, Moon, Home, Flower2,
  RefreshCw, Loader2, Clock, Heart, Music, Brain,
  Sparkles, ChevronRight
} from "lucide-react";

const CATEGORY_ICONS = {
  nature: TreePine,
  space: Moon,
  indoor: Home,
  meditation: Brain,
  classical: Music,
  ambient: Waves,
  spiritual: Sparkles,
  instrumental: Music
};

export default function VRComfortDashboard() {
  const [loading, setLoading] = useState(true);
  const [experiences, setExperiences] = useState([]);
  const [meditations, setMeditations] = useState([]);
  const [music, setMusic] = useState([]);
  const [stats, setStats] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  
  // Session state
  const [activeSession, setActiveSession] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState([70]);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [rating, setRating] = useState([3]);
  const [feedback, setFeedback] = useState("");
  const [sessionLoading, setSessionLoading] = useState(false);
  
  const audioRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [expRes, medRes, musicRes, statsRes, recRes] = await Promise.all([
        api.get("/phase71/experiences"),
        api.get("/phase71/meditations"),
        api.get("/phase71/music"),
        api.get("/phase71/stats"),
        api.get("/phase71/recommendations")
      ]);
      
      setExperiences(expRes.data.experiences || []);
      setMeditations(medRes.data.meditations || []);
      setMusic(musicRes.data.playlists || []);
      setStats(statsRes.data);
      setRecommendations(recRes.data.recommendations || []);
    } catch (error) {
      console.error("Error loading VR data:", error);
      toast.error("Failed to load comfort experiences");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const startSession = async (type, id) => {
    setSessionLoading(true);
    try {
      const res = await api.post("/phase71/sessions/start", {
        experience_type: type,
        experience_id: id
      });
      
      setActiveSession({
        ...res.data.session,
        experience: res.data.experience
      });
      setIsPlaying(true);
      toast.success(`Starting ${res.data.experience.name}`);
      
      // In a real app, this would load actual VR/audio content
      // For demo, we simulate the experience
    } catch (error) {
      toast.error("Failed to start session");
    } finally {
      setSessionLoading(false);
    }
  };

  const endSession = async () => {
    if (!activeSession) return;
    
    try {
      await api.post(`/phase71/sessions/${activeSession.id}/end`, {
        comfort_rating: rating[0],
        feedback: feedback || null
      });
      
      toast.success("Session completed. Thank you for your feedback!");
      setActiveSession(null);
      setIsPlaying(false);
      setShowEndDialog(false);
      setRating([3]);
      setFeedback("");
      loadData(); // Refresh stats
    } catch (error) {
      toast.error("Failed to save session");
    }
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
    // In real app, would control actual media playback
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="vr-comfort-loading">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto" />
          <p className="text-slate-600 font-medium">Loading comfort experiences...</p>
        </div>
      </div>
    );
  }

  // Active session view
  if (activeSession) {
    const exp = activeSession.experience;
    return (
      <div 
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: exp.color_theme || "#0d9488" }}
        data-testid="vr-active-session"
      >
        {/* Session Header */}
        <header className="p-4 flex justify-between items-center text-white">
          <div>
            <h1 className="text-xl font-bold">{exp.name}</h1>
            <p className="text-white/80 text-sm">{exp.description}</p>
          </div>
          <Button 
            variant="outline" 
            className="bg-white/20 border-white/30 text-white hover:bg-white/30"
            onClick={() => setShowEndDialog(true)}
          >
            <X className="w-4 h-4 mr-2" />
            End Session
          </Button>
        </header>

        {/* Main Experience Area */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-white space-y-8">
            {/* Animated visual representation */}
            <div className="relative w-64 h-64 mx-auto">
              <div 
                className={`absolute inset-0 rounded-full bg-white/20 ${isPlaying ? "animate-pulse" : ""}`}
                style={{ animationDuration: "3s" }}
              />
              <div 
                className={`absolute inset-8 rounded-full bg-white/30 ${isPlaying ? "animate-pulse" : ""}`}
                style={{ animationDuration: "2s", animationDelay: "0.5s" }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                {exp.category === "nature" && <TreePine className="w-24 h-24 text-white/80" />}
                {exp.category === "space" && <Moon className="w-24 h-24 text-white/80" />}
                {exp.category === "indoor" && <Home className="w-24 h-24 text-white/80" />}
              </div>
            </div>

            {/* Play controls */}
            <div className="flex items-center justify-center gap-4">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full w-16 h-16 bg-white/20 border-white/30 text-white hover:bg-white/30"
                onClick={togglePlay}
              >
                {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
              </Button>
            </div>

            {/* Volume control */}
            <div className="flex items-center justify-center gap-4 max-w-xs mx-auto">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={toggleMute}
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>
              <Slider
                value={volume}
                onValueChange={setVolume}
                max={100}
                step={1}
                className="flex-1"
              />
            </div>

            {/* Duration */}
            <p className="text-white/60">
              <Clock className="w-4 h-4 inline mr-2" />
              {exp.duration_minutes} minutes
            </p>
          </div>
        </div>

        {/* Ambient sounds indicator */}
        {exp.ambient_sounds && (
          <div className="p-4 text-center text-white/60 text-sm">
            Ambient: {exp.ambient_sounds.join(", ")}
          </div>
        )}

        {/* End Session Dialog */}
        <Dialog open={showEndDialog} onOpenChange={setShowEndDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>How was your experience?</DialogTitle>
              <DialogDescription>
                Your feedback helps us improve comfort experiences for everyone.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Comfort Rating</Label>
                <div className="flex items-center gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating([star])}
                      className="focus:outline-none"
                    >
                      <Star 
                        className={`w-8 h-8 ${star <= rating[0] ? "text-yellow-400 fill-yellow-400" : "text-slate-300"}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Feedback (optional)</Label>
                <Textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="What did you find most helpful?"
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEndDialog(false)}>
                Continue Session
              </Button>
              <Button onClick={endSession}>
                End & Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main dashboard view
  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white p-6" data-testid="vr-comfort-dashboard">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <h1 className="text-3xl font-bold text-slate-900">VR Comfort Experiences</h1>
        <p className="text-slate-600 mt-2">
          Immersive environments, guided meditations, and music therapy for comfort and relaxation.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="max-w-6xl mx-auto mb-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="vr-stats">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-teal-600">{stats.total_sessions}</p>
              <p className="text-sm text-slate-600">Total Sessions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-teal-600">{stats.sessions_this_week}</p>
              <p className="text-sm text-slate-600">This Week</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-teal-600">{stats.average_comfort_rating || "-"}</p>
              <p className="text-sm text-slate-600">Avg Rating</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-lg font-bold text-teal-600">{stats.favorite_experience || "None yet"}</p>
              <p className="text-sm text-slate-600">Favorite</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-6xl mx-auto">
        <Tabs defaultValue="experiences">
          <TabsList className="mb-6">
            <TabsTrigger value="experiences">VR Experiences</TabsTrigger>
            <TabsTrigger value="meditations">Guided Meditations</TabsTrigger>
            <TabsTrigger value="music">Music Therapy</TabsTrigger>
            <TabsTrigger value="recommended">For You</TabsTrigger>
          </TabsList>

          {/* VR Experiences */}
          <TabsContent value="experiences">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vr-experiences-grid">
              {experiences.map((exp) => {
                const IconComponent = CATEGORY_ICONS[exp.category] || Sparkles;
                return (
                  <Card key={exp.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div 
                      className="h-32 flex items-center justify-center"
                      style={{ backgroundColor: exp.color_theme }}
                    >
                      <IconComponent className="w-16 h-16 text-white/80" />
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-slate-900">{exp.name}</h3>
                          <p className="text-sm text-slate-600 mt-1">{exp.description}</p>
                        </div>
                        <Badge variant="outline">{exp.duration_minutes}m</Badge>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-1">
                        {exp.recommended_for?.slice(0, 3).map((rec) => (
                          <Badge key={rec} variant="secondary" className="text-xs">
                            {rec.replace("_", " ")}
                          </Badge>
                        ))}
                      </div>
                      <Button 
                        className="w-full mt-4" 
                        onClick={() => startSession("vr", exp.id)}
                        disabled={sessionLoading}
                      >
                        {sessionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                        Start Experience
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Guided Meditations */}
          <TabsContent value="meditations">
            <div className="grid md:grid-cols-2 gap-6" data-testid="meditations-grid">
              {meditations.map((med) => (
                <Card key={med.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Brain className="w-7 h-7 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-slate-900">{med.name}</h3>
                        <p className="text-sm text-slate-600 mt-1">{med.description}</p>
                        <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                          <Clock className="w-4 h-4" />
                          <span>{med.duration_minutes} minutes</span>
                          <Badge variant="outline" className="ml-2">{med.category}</Badge>
                        </div>
                      </div>
                    </div>
                    <Button 
                      className="w-full mt-4" 
                      variant="outline"
                      onClick={() => startSession("meditation", med.id)}
                      disabled={sessionLoading}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Begin Meditation
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Music Therapy */}
          <TabsContent value="music">
            <div className="grid md:grid-cols-2 gap-6" data-testid="music-grid">
              {music.map((playlist) => {
                const IconComponent = CATEGORY_ICONS[playlist.category] || Music;
                return (
                  <Card key={playlist.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-7 h-7 text-teal-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-slate-900">{playlist.name}</h3>
                          <p className="text-sm text-slate-600 mt-1">{playlist.description}</p>
                          <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                            <Clock className="w-4 h-4" />
                            <span>{playlist.duration_minutes} minutes</span>
                          </div>
                        </div>
                      </div>
                      <Button 
                        className="w-full mt-4" 
                        variant="outline"
                        onClick={() => startSession("music", playlist.id)}
                        disabled={sessionLoading}
                      >
                        <Music className="w-4 h-4 mr-2" />
                        Play Music
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Recommendations */}
          <TabsContent value="recommended">
            <div className="space-y-4" data-testid="recommendations-list">
              <p className="text-slate-600">
                Personalized recommendations based on patient needs and preferences.
              </p>
              {recommendations.slice(0, 6).map((rec, idx) => {
                const exp = rec.experience;
                const IconComponent = CATEGORY_ICONS[exp.category] || Sparkles;
                return (
                  <Card key={idx} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-16 h-16 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: exp.color_theme || "#0d9488" }}
                        >
                          <IconComponent className="w-8 h-8 text-white/80" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-slate-900">{exp.name}</h3>
                          <p className="text-sm text-slate-600">{exp.description}</p>
                          {rec.reason && (
                            <p className="text-xs text-teal-600 mt-1">{rec.reason}</p>
                          )}
                        </div>
                        <Button 
                          variant="ghost"
                          onClick={() => startSession(rec.type || "vr", exp.id)}
                        >
                          <ChevronRight className="w-5 h-5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
